import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Link } from "react-router-dom";
import {
  adminFetchBtwMailContext,
  adminSendBtwCustomerEmail,
  adminFetchBtwEmailTemplates,
} from "../core/axios/endpoints";
import { useBodyScrollLock } from "../core/scroll-lock";
import RichTextEditor from "./rich-text-editor";

// Templates uit DB zijn plain text met \n line-breaks (legacy). Voor TipTap
// converteren we ze naar HTML met <p>-tags per paragraaf. Als de template al
// HTML-tags bevat, laten we 'm ongemoeid.
const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const toEditorHtml = (text) => {
  if (!text) return "";
  if (/<\/?[a-z][^>]*>/i.test(text)) return text;
  return text
    .split(/\n\s*\n/)
    .map((p) => escapeHtml(p).replace(/\n/g, "<br />"))
    .map((p) => `<p>${p}</p>`)
    .join("");
};

// Zelfde regex als de server (utils/btwMailPlaceholders.ts). Alleen voor
// het voorbeeld en de waarschuwingen; de server vult de echte mail in.
const PLACEHOLDER_RE = /\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g;
const findPlaceholders = (text) => {
  const keys = new Set();
  let m;
  PLACEHOLDER_RE.lastIndex = 0;
  while ((m = PLACEHOLDER_RE.exec(text ?? "")) !== null) keys.add(m[1]);
  return Array.from(keys);
};
const escapeHtmlValue = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
const fillPlaceholders = (text, values, html) =>
  (text ?? "").replace(PLACEHOLDER_RE, (match, key) => {
    if (!(key in values)) return match;
    return html ? escapeHtmlValue(values[key]) : values[key];
  });

const GROEP_LABELS = {
  klant: "Klant",
  aanvraag: "Aanvraag",
  berekening: "Berekening",
};

// `api` maakt de modal herbruikbaar voor subsidie-aanvragen: eigen templates
// (scope), variabelen en verzend-endpoint. Zonder `api` gedraagt hij zich als
// voorheen (BTW).
const SendCustomerEmailModal = ({
  isOpen,
  applicationId,
  customerName,
  customerEmail,
  onClose,
  onSent,
  api = {},
}) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [toEmail, setToEmail] = useState(customerEmail ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [mailCtx, setMailCtx] = useState(null);
  const [showVars, setShowVars] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const editorRef = useRef(null);
  const onEditorReady = useCallback((ed) => {
    editorRef.current = ed;
  }, []);

  useEffect(() => {
    if (!isOpen || !applicationId) return undefined;
    let cancelled = false;
    (api.fetchMailContext ?? adminFetchBtwMailContext)(applicationId)
      .then((res) => {
        if (!cancelled) setMailCtx(res?.data ?? res);
      })
      .catch(() => {
        if (!cancelled) setMailCtx(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, applicationId]);

  const insertVariable = (key) => {
    const ed = editorRef.current;
    const token = `{{${key}}}`;
    if (ed && !ed.isDestroyed) {
      ed.chain().focus().insertContent(token).run();
    } else {
      setBody((b) => `${b}${token}`);
    }
  };

  const placeholders = mailCtx?.placeholders ?? {};
  const calc = mailCtx?.calc;
  const usedKeys = useMemo(
    () => Array.from(new Set([...findPlaceholders(subject), ...findPlaceholders(body)])),
    [subject, body],
  );
  const knownKeys = useMemo(
    () => new Set((mailCtx?.definities ?? []).map((d) => d.key)),
    [mailCtx],
  );
  const berekeningKeys = useMemo(
    () =>
      new Set(
        (mailCtx?.definities ?? [])
          .filter((d) => d.groep === "berekening")
          .map((d) => d.key),
      ),
    [mailCtx],
  );
  const unknownKeys = usedKeys.filter((k) => mailCtx && !knownKeys.has(k));
  // Optionele variabelen (tussenvoegsel, partnerNaam) mogen leeg zijn; de
  // server blokkeert daar ook niet op.
  const optionalKeys = useMemo(
    () =>
      new Set(
        (mailCtx?.definities ?? []).filter((d) => d.optioneel).map((d) => d.key),
      ),
    [mailCtx],
  );
  const emptyKeys = usedKeys.filter(
    (k) => knownKeys.has(k) && !placeholders[k] && !optionalKeys.has(k),
  );
  const usesBerekening = usedKeys.some((k) => berekeningKeys.has(k));
  const berekeningBlokkeert = usesBerekening && calc && !calc.compleet;
  const previewSubject = fillPlaceholders(subject, placeholders, false);
  const previewBody = fillPlaceholders(body, placeholders, true);
  const varProblem =
    unknownKeys.length > 0 || emptyKeys.length > 0 || berekeningBlokkeert;

  useBodyScrollLock(isOpen);

  // Templates ophalen bij open. We cachen niet bewust - admin kan ze net
  // bewerken hebben en we willen direct de verse versie.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setTemplatesLoading(true);
    (api.fetchTemplates ?? adminFetchBtwEmailTemplates)()
      .then((res) => {
        if (cancelled) return;
        setTemplates(res?.data ?? res ?? []);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg =
          e?.response?.data?.error ?? e?.message ?? "Templates laden mislukt.";
        setError(msg);
        setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSubject("");
      setBody("");
      setError(null);
    } else {
      // Bij open reset het naar-adres naar het adres uit de aanvraag, tenzij
      // admin het expliciet in deze sessie heeft aangepast.
      setToEmail(customerEmail ?? "");
    }
  }, [isOpen, customerEmail]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, submitting, onClose]);

  const applyTemplate = (tpl) => {
    setSubject(tpl.subject ?? "");
    setBody(toEditorHtml(tpl.body ?? ""));
  };

  // TipTap output bevat HTML-tags. Voor de minimum-lengte-check willen we
  // alleen de zichtbare tekst tellen.
  const bodyTextLength = body
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim().length;

  const submit = async () => {
    if (subject.trim().length < 3 || bodyTextLength < 10) {
      setError("Onderwerp + bericht moeten gevuld zijn.");
      return;
    }
    const trimmedTo = toEmail.trim();
    if (!trimmedTo || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedTo)) {
      setError("Geef een geldig e-mailadres op om naar te sturen.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await (api.sendEmail ?? adminSendBtwCustomerEmail)(applicationId, {
        subject: subject.trim(),
        body: body.trim(),
        toEmail: trimmedTo,
      });
      onSent?.({ subject: subject.trim(), toEmail: trimmedTo });
      onClose();
    } catch (e) {
      setError(
        e?.response?.data?.error ?? e?.message ?? "Mail versturen mislukt.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  if (typeof document === "undefined") return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-[#091E428A] p-4">
      <div className="my-8 w-full max-w-2xl rounded-md bg-white shadow-overlay">
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-medium text-ink">
              Mail naar klant
            </h2>
            <p className="mt-1 text-sm text-ink-subtlest">
              Stuur een persoonlijk bericht aan <strong>{customerName}</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded text-ink-subtlest hover:bg-[#091E420F] disabled:opacity-50"
            aria-label="Sluiten"
          >
            <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M4.3 4.3a1 1 0 011.4 0L10 8.6l4.3-4.3a1 1 0 111.4 1.4L11.4 10l4.3 4.3a1 1 0 11-1.4 1.4L10 11.4l-4.3 4.3a1 1 0 11-1.4-1.4L8.6 10 4.3 5.7a1 1 0 010-1.4z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 text-sm text-ink">
          {/* Naar-adres (aanpasbaar) */}
          <label className="mb-5 block text-sm">
            <span className="text-xs font-semibold text-ink-subtlest">Naar</span>
            <div className="relative mt-1">
              <input
                type="email"
                value={toEmail}
                onChange={(e) => setToEmail(e.target.value)}
                disabled={submitting}
                placeholder="naam@voorbeeld.nl"
                className="w-full rounded border-2 border-line bg-white px-2 py-1.5 pr-20 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
              />
              {customerEmail && toEmail !== customerEmail ? (
                <button
                  type="button"
                  onClick={() => setToEmail(customerEmail)}
                  disabled={submitting}
                  className="absolute inset-y-0 right-2 my-auto h-6 rounded px-2 text-xs font-medium text-brand hover:bg-[#091E420F]"
                >
                  reset
                </button>
              ) : null}
            </div>
            {customerEmail && toEmail && toEmail !== customerEmail ? (
              <p className="mt-1 text-xs text-[#7F5F01]">
                Let op: dit is een ander adres dan dat van de aanvraag ({customerEmail}).
              </p>
            ) : null}
          </label>

          {/* Templates uit DB */}
          <div className="mb-5">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                Snel beginnen met
              </p>
              <Link
                to="/panel/admin/btw-email-templates"
                onClick={onClose}
                className="text-xs text-brand hover:underline"
              >
                Templates beheren &rarr;
              </Link>
            </div>
            {templatesLoading ? (
              <p className="text-xs text-ink-subtlest">Templates laden…</p>
            ) : templates.length === 0 ? (
              <p className="text-xs text-ink-subtlest">
                Geen templates. Maak ze aan via &quot;Templates beheren&quot;.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {templates.map((tpl) => (
                  <button
                    key={tpl._id}
                    type="button"
                    onClick={() => applyTemplate(tpl)}
                    disabled={submitting}
                    className="rounded bg-[#091E420F] px-2.5 py-1 text-xs font-medium text-ink-subtle hover:bg-brand-subtle hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {tpl.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <label className="mb-4 block text-sm">
            <span className="text-xs font-semibold text-ink-subtlest">Onderwerp</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Onderwerp van de mail"
              className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink placeholder:text-ink-subtlest focus:border-brand focus:outline-none disabled:opacity-50"
              disabled={submitting}
            />
          </label>

          <div className="block text-sm">
            <span className="text-xs font-semibold text-ink-subtlest">Bericht</span>
            <div className="mt-1">
              <RichTextEditor
                value={body}
                onChange={setBody}
                onReady={onEditorReady}
                disabled={submitting}
                placeholder="Schrijf hier de boodschap aan de klant. Gebruik vet, lijsten of links waar nuttig."
              />
            </div>
          </div>

          {/* Variabelen: de server vult {{...}} in bij verzenden. */}
          <div className="mt-3 rounded bg-sunken px-3 py-2 text-xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setShowVars((v) => !v)}
                className="text-xs font-semibold text-ink hover:text-brand"
              >
                {showVars ? "▾" : "▸"} Variabelen invoegen
              </button>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                disabled={!mailCtx}
                className="h-7 rounded bg-[#091E420F] px-2 text-xs font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {showPreview ? "Voorbeeld verbergen" : "Voorbeeld met echte waarden"}
              </button>
            </div>
            {showVars ? (
              <div className="mt-2 space-y-2">
                {["klant", "aanvraag", "berekening"].map((groep) => (
                  <div key={groep}>
                    <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                      {GROEP_LABELS[groep]}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {(mailCtx?.definities ?? [])
                        .filter((d) => d.groep === groep)
                        .map((d) => {
                          const val = placeholders[d.key];
                          return (
                            <button
                              key={d.key}
                              type="button"
                              onClick={() => insertVariable(d.key)}
                              disabled={submitting}
                              title={`${d.label}${
                                val
                                  ? `: ${val}`
                                  : d.optioneel
                                    ? " (leeg voor deze aanvraag, valt weg)"
                                    : " (geen waarde voor deze aanvraag)"
                              }`}
                              className={`rounded px-2 py-0.5 text-[11px] font-medium ${
                                val || d.optioneel
                                  ? "bg-white text-ink shadow-card hover:bg-brand-subtle hover:text-brand"
                                  : "bg-[#FFF7D6] text-[#7F5F01]"
                              }`}
                            >
                              {`{{${d.key}}}`}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                ))}
                {calc?.compleet ? (
                  <p className="text-[11px] text-ink-subtlest">
                    Berekening: BTW op factuur {placeholders.btwFactuur}, afdracht
                    eerste jaar {placeholders.afdrachtEersteJaar} ({placeholders.afdrachtPerJaar} per
                    jaar x {calc.eersteJaar?.dagenActief}/{calc.eersteJaar?.dagenInJaar} dagen vanaf
                    {" "}{placeholders.startdatumAfdracht || placeholders.installatiedatum}), netto {placeholders.nettoTeruggave}.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          {berekeningBlokkeert ? (
            <div className="mt-3 rounded bg-[#FFF7D6] px-3 py-2 text-sm text-[#7F5F01]">
              <strong>Bedragen ontbreken.</strong> Deze mail gebruikt de berekening,
              maar die kan niet gemaakt worden: {calc.warnings.join(" ")} Vul eerst
              het aankoopbedrag en de capaciteit aan bij &quot;Gegevens voor
              BTW-teruggave&quot;.
            </div>
          ) : null}
          {unknownKeys.length > 0 ? (
            <div className="mt-3 rounded bg-[#FFF7D6] px-3 py-2 text-sm text-[#7F5F01]">
              <strong>Onbekende variabele(n):</strong>{" "}
              {unknownKeys.map((k) => `{{${k}}}`).join(", ")}. Kies een variabele uit
              de lijst hierboven.
            </div>
          ) : null}
          {emptyKeys.length > 0 && !berekeningBlokkeert ? (
            <div className="mt-3 rounded bg-[#FFF7D6] px-3 py-2 text-sm text-[#7F5F01]">
              <strong>Geen waarde voor deze aanvraag:</strong>{" "}
              {emptyKeys.map((k) => `{{${k}}}`).join(", ")}. Haal ze uit de mail of vul
              de gegevens aan.
            </div>
          ) : null}

          {showPreview && mailCtx ? (
            <div className="mt-3 rounded border border-line bg-white p-3">
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
                Voorbeeld zoals de klant het ontvangt
              </p>
              <p className="text-sm font-semibold text-ink">{previewSubject || "(geen onderwerp)"}</p>
              <p className="mt-2 text-sm text-ink-subtle">Beste {placeholders.aanhef},</p>
              <div
                // Zelfde opmaakregels als het style-blok in de mail-shell
                // (templates/partials/header.html), zodat het voorbeeld klopt
                // met wat de klant ontvangt.
                className="prose-sm mt-2 max-w-none text-sm text-gray-900 [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:text-[15px] [&_h3]:font-bold [&_h3]:text-gray-900 [&_h3:first-child]:mt-0 [&_strong]:font-bold [&_strong]:text-gray-900 [&_a]:text-[#1F845A] [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-brand [&_blockquote]:bg-[#DCFFF1]/60 [&_blockquote]:py-2 [&_blockquote]:pl-3"
                // Body is de eigen editor-output (TipTap), de ingevulde waarden
                // zijn ge-escaped. De server sanitized bij verzenden alsnog.
                dangerouslySetInnerHTML={{ __html: previewBody }}
              />
            </div>
          ) : null}

          <div className="mt-3 rounded bg-brand-subtle px-3 py-2 text-xs text-brand">
            <strong>Info:</strong> de mail wordt verstuurd vanaf{" "}
            <span className="font-medium">noreply@email.subsidie2gether.nl</span>{" "}
            met reply-to naar <span className="font-medium">info@subsidie2gether.nl</span>.
            Klant beantwoordt automatisch naar info-inbox.
          </div>

          {error ? (
            <div className="mt-3 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 pb-6 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="h-8 rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={
              submitting ||
              subject.trim().length < 3 ||
              bodyTextLength < 10 ||
              varProblem
            }
            className="h-8 rounded bg-brand px-3 text-sm font-medium text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Verzenden..." : "Verstuur mail"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SendCustomerEmailModal;
